import { useState, useEffect, useCallback } from 'react'
import { resolveIcon } from '@/lib/icon-resolver'
import { getAppsForUser } from '@/data/apps'
import { roleManagementService } from '@/services/role-management-service'
import { authService } from '@/services/auth-service'
import { externalUrlAppsService } from '@/services/external-url-apps-service'
import { myAppsService, type MyApp } from '@/services/my-apps-service'
import { logInfo, logError } from '@/lib/logger'

/** Admin-defined URL apps the user has, as AppDef-compatible objects. */
async function toUrlApps(myApps: MyApp[]) {
  const urlApps = myApps.filter((app) => app.kind === 'url' && app.external_url)

  // Register display names as i18n resources so t('apps.<slug>') works in Start Menu, App Store etc.
  if (urlApps.length > 0) {
    const { default: i18n } = await import('@/i18n/config')
    for (const app of urlApps) {
      i18n.addResource('de', 'translation', `apps.${app.name}`, app.display_name)
      i18n.addResource('en', 'translation', `apps.${app.name}`, app.display_name)
    }
  }

  return urlApps.map((app) => ({
    id: app.name,
    Icon: resolveIcon(app.icon || 'Globe'),
    implemented: true,
    externalUrl: app.external_url,
  }))
}

// Current user as provided by authService (Azure AD)
interface CurrentUser {
  id: string
  email: string
  role: string
  isActive: boolean
  name?: string
}

/**
 * The apps the signed-in user has: the BFF's `/apps/me` list (#1610) mapped
 * onto the registry in `src/data/apps.ts`, plus admin-defined URL apps.
 */
export function useUserApps() {
  const [userApps, setUserApps] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [isSupervisor, setIsSupervisor] = useState(false)

  const loadUserApps = useCallback(async () => {
    try {
      setIsLoading(true)

      const cachedUser = authService.getCachedUser()
      if (!cachedUser) {
        logInfo('useUserApps: Kein Benutzer angemeldet')
        setUser(null)
        setUserApps([])
        return
      }

      const currentUser: CurrentUser = {
        id: cachedUser.id,
        email: cachedUser.email,
        role: cachedUser.role || 'user',
        isActive: cachedUser.isActive ?? true,
        name: cachedUser.name,
      }
      setUser(currentUser)

      const role = (currentUser.role === 'admin' ? 'admin' : 'user') as 'admin' | 'user'

      let supervisorStatus = false
      if (role !== 'admin') {
        try {
          const status = await roleManagementService.getSupervisorStatus()
          supervisorStatus = status.is_supervisor
          setIsSupervisor(supervisorStatus)
        } catch {
          // Ignore errors - user is not a supervisor
        }
      }

      const myApps = await myAppsService.getMyApps()
      const registryApps = await getAppsForUser(myApps.map((app) => app.name), role, supervisorStatus)
      const urlApps = await toUrlApps(myApps)
      const seenIds = new Set<string>(registryApps.map((a) => a.id))
      setUserApps([...registryApps, ...urlApps.filter((app) => !seenIds.has(app.id))])
    } catch (error) {
      logError(error as Error, { context: 'useUserApps.loadUserApps' })
      setUserApps([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUserApps()

    const handleExternalUrlAppsChanged = () => {
      externalUrlAppsService.invalidateCache()
      myAppsService.invalidate()
      loadUserApps()
    }
    window.addEventListener('external-url-apps-changed', handleExternalUrlAppsChanged)
    return () => {
      window.removeEventListener('external-url-apps-changed', handleExternalUrlAppsChanged)
    }
  }, [loadUserApps])

  return {
    userApps,
    isLoading,
    user,
    isSupervisor,
    refreshApps: () => {
      externalUrlAppsService.invalidateCache()
      myAppsService.invalidate()
      void loadUserApps()
    },
  }
}
